import {useCallback, useEffect, useMemo, useState} from 'react'
import {SetURLSearchParams, useSearchParams} from 'react-router'
import {useLocalStorage} from 'react-use'

type Options = {
  keepInLocalStorage?: boolean
  enabled?: boolean
}

export const useQueryParamValue = <T extends string>(
  key: string,
  options: Options = {}
): [T | undefined, (value: T | undefined) => void] => {
  const {keepInLocalStorage, enabled = true} = options

  const [storageValue, setStorageValue, removeStorageValue] = useLocalStorage<T>(key)
  const [params, setParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()
  const paramValue = params.get(key) as T | null
  const [isInitialized, setIsInitialized] = useState(false)

  const updateUrlParams = useCallback(
    (newValue: T | undefined) => {
      setParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams.toString())
          if (newValue) {
            newParams.set(key, newValue)
          } else {
            newParams.delete(key)
          }
          return newParams
        },
        {replace: true, flushSync: true}
      )
    },
    [setParams, key]
  )

  const updateLocalStorage = useCallback(
    (newValue: T | undefined) => {
      if (newValue) {
        setStorageValue(newValue)
      } else {
        removeStorageValue()
      }
    },
    [setStorageValue, removeStorageValue]
  )

  useEffect(() => {
    if (isInitialized) return

    const shouldRestoreFromStorage =
      enabled && keepInLocalStorage && !isInitialized && !paramValue && storageValue

    if (shouldRestoreFromStorage) {
      updateUrlParams(storageValue)
    }

    const shouldSyncToStorage =
      enabled && keepInLocalStorage && paramValue && paramValue !== storageValue

    if (shouldSyncToStorage) {
      setStorageValue(paramValue)
    }

    setIsInitialized(true)
  }, [
    isInitialized,
    keepInLocalStorage,
    enabled,
    paramValue,
    storageValue,
    updateUrlParams,
    setStorageValue
  ])

  useEffect(() => {
    const shouldClearUrlParam = !enabled && paramValue
    if (shouldClearUrlParam) {
      setParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams.toString())
          newParams.delete(key)
          return newParams
        },
        {replace: true, flushSync: true}
      )
    }
  }, [setParams, key, paramValue, enabled])

  const setValue = useCallback(
    (newValue: T | undefined) => {
      if (keepInLocalStorage) {
        updateLocalStorage(newValue)
      }
      updateUrlParams(newValue)
    },
    [updateUrlParams, updateLocalStorage, keepInLocalStorage]
  )

  const value = useMemo<T | undefined>(() => {
    if (!enabled) return undefined

    return paramValue ?? (keepInLocalStorage ? storageValue : undefined)
  }, [paramValue, storageValue, enabled, keepInLocalStorage])

  return [value, setValue]
}
